import React from "react";
import { Descriptions, Empty, List, Modal, Space, Tag, Typography } from "antd";
import { attendancePlannerPolicyDependencies } from "../policyDependencyConfig";

const { Paragraph, Text, Title } = Typography;

const getName = (item) => item?.name || item?.title || item?.code || `#${item?.id}`;

const findById = (items, id) => (items || []).find((item) => `${item.id}` === `${id}`);

const ruleSubject = {
  absentPolicy: "absence",
  missingPolicy: "missing attendance",
  lateArrivalPolicy: "late arrival minutes",
  earlyLeftPolicy: "early leaving minutes",
  shortDurationPolicy: "short working minutes",
  relaxationPolicy: "relaxation minutes",
  leavePolicy: "deductible leave",
  overtimePolicy: "approved overtime minutes",
  cplPolicy: "approved overtime minutes for CPL",
};

const effectWord = (dependency) => (
  dependency?.key === "overtimePolicy"
    ? "pays"
    : dependency?.key === "cplPolicy"
      ? "adds compensatory leave"
      : "deducts"
);

const rangeText = (slab, dependency) => {
  if (dependency?.key === "overtimePolicy" || dependency?.key === "cplPolicy") {
    return slab.max
      ? `from ${slab.min} to ${slab.max} approved minutes`
      : `from ${slab.min} approved minutes and above`;
  }

  const minutes = slab.end_mins
    ? `from ${slab.start_mins} to ${slab.end_mins} minutes`
    : slab.start_mins
      ? `from ${slab.start_mins} minutes and above`
      : null;

  const occurrences = slab.max_times
    ? `from occurrence ${slab.min_times} to ${slab.max_times}`
    : slab.min_times
      ? `from occurrence ${slab.min_times} and above`
      : null;

  return [minutes, occurrences].filter(Boolean).join(", ");
};

const repeatText = (slab, dependency) => {
  if (dependency?.key === "overtimePolicy" || dependency?.key === "cplPolicy") {
    return "";
  }

  if (!slab.repeat_id && !slab.repeat_name) {
    return "";
  }

  return `Repeat rule: ${slab.repeat_name || `repeat #${slab.repeat_id}`}.`;
};

const effectText = (slab, dependency, relatedData) => {
  const isEarning = dependency?.key === "overtimePolicy" || dependency?.key === "cplPolicy";
  const effect = isEarning
    ? findById(relatedData?.encashment_types, slab.encashment_type_id)
    : findById(relatedData?.deduction_types, slab.deduction_type_id);
  const fallback = findById(relatedData?.deduction_types, slab.fallback_type_id)
    || findById(relatedData?.encashment_types, slab.fallback_type_id);

  const main = effect
    ? `${effectWord(dependency)} using "${getName(effect)}"`
    : `${effectWord(dependency)} using the selected salary effect`;

  if (!fallback) {
    return main;
  }

  return `${main}. If quota is not enough, salary fallback uses "${getName(fallback)}"`;
};

export const RulePreview = ({ dependency, record, relatedData }) => {
  const slabs = record?.slabs || [];
  const subject = ruleSubject[dependency?.key] || dependency?.label?.toLowerCase() || "attendance";

  return (
    <Space direction="vertical" size={ 16 } style={ { width: "100%" } }>
      <div>
        <Title level={ 5 }>{ getName(record) }</Title>
        <Paragraph type="secondary">
          This rule is checked during payroll. When an employee's { subject } matches a slab, the system { effectWord(dependency) } salary, quota, overtime, or CPL according to that slab.
        </Paragraph>
      </div>

      <Descriptions size="small" column={ 1 } bordered>
        <Descriptions.Item label="Rule type">{ dependency?.label || "Policy rule" }</Descriptions.Item>
        <Descriptions.Item label="Saved note">{ record?.description || "No note added" }</Descriptions.Item>
        <Descriptions.Item label="Slabs">{ slabs.length }</Descriptions.Item>
      </Descriptions>

      { slabs.length ? (
        <List
          size="small"
          dataSource={ slabs }
          renderItem={ (slab, index) => (
            <List.Item>
              <Space direction="vertical" size={ 4 }>
                <Text strong>Slab { index + 1 }: { rangeText(slab, dependency) || "matching attendance" }</Text>
                <Text>{ effectText(slab, dependency, relatedData) }.</Text>
                { repeatText(slab, dependency) ? <Text type="secondary">{ repeatText(slab, dependency) }</Text> : null }
              </Space>
            </List.Item>
          ) }
        />
      ) : (
        <Empty image={ Empty.PRESENTED_IMAGE_SIMPLE } description="No slabs found for this rule" />
      ) }
    </Space>
  );
};

export const PackagePreview = ({ record, relatedData }) => {
  const selectedRules = attendancePlannerPolicyDependencies
    .map((dependency) => {
      const ruleId = record?.[dependency.field];
      const rule = findById(relatedData?.[dependency.relatedListKey], ruleId);
      return ruleId ? { dependency, ruleId, rule } : null;
    })
    .filter(Boolean);

  return (
    <Space direction="vertical" size={ 16 } style={ { width: "100%" } }>
      <div>
        <Title level={ 5 }>{ getName(record) }</Title>
        <Paragraph type="secondary">
          This package decides which attendance rules apply to employees from the effective date.
        </Paragraph>
      </div>

      <Descriptions size="small" column={ 1 } bordered>
        <Descriptions.Item label="Effective from">{ record?.date || "Not available" }</Descriptions.Item>
        <Descriptions.Item label="Employees">{ record?.for_all ? "All employees" : "Selected employees" }</Descriptions.Item>
        <Descriptions.Item label="Rules attached">{ selectedRules.length }</Descriptions.Item>
      </Descriptions>

      { selectedRules.length ? (
        <List
          size="small"
          dataSource={ selectedRules }
          renderItem={ ({ dependency, rule, ruleId }) => (
            <List.Item>
              <Space>
                <Tag>{ dependency.label }</Tag>
                <Text>{ rule ? getName(rule) : `Rule #${ruleId}` }</Text>
              </Space>
            </List.Item>
          ) }
        />
      ) : (
        <Empty image={ Empty.PRESENTED_IMAGE_SIMPLE } description="No rules attached to this package" />
      ) }
    </Space>
  );
};

const AttendanceRulePreview = ({ open, preview, onClose }) => (
  <Modal
    open={ open }
    title={ preview?.type === "package" ? "Policy package preview" : "Rule preview" }
    onCancel={ onClose }
    footer={ null }
    width={ 820 }
    destroyOnClose
  >
    { preview?.type === "package" ? (
      <PackagePreview record={ preview.record } relatedData={ preview.relatedData } />
    ) : (
      <RulePreview dependency={ preview?.dependency } record={ preview?.record } relatedData={ preview?.relatedData } />
    ) }
  </Modal>
);

export default AttendanceRulePreview;
